import { useEffect, useMemo, useCallback, useState } from "react";
import { useMultiTableData } from "./hooks/useMultiTableData.jsx";
import { useSnackbar } from "notistack";
import { Box, Typography, Tooltip, IconButton, Badge } from "@mui/material";
import {
  Circle,
  PriceChangeOutlined,
  PriceCheckOutlined,
  PendingActionsOutlined,
  AddPhotoAlternateOutlined,
  PostAddOutlined,
  MarkEmailUnreadOutlined,
  ForwardToInboxOutlined,
  VisibilityOffOutlined,
  LockOutlined,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useSetRecoilState } from "recoil";
import DataTable from "../DataTable.jsx";
import ViewAllLink from "../ViewAllLink.jsx";
import { getDatumZeit } from "../../services/formatService.js";
import { useUser } from "../../hooks/user.js";
import { getUserRowsPerPage } from "../../utils/userSettings.js";
import { backendGet, backendPost } from "../../services/dataService.js";
import { notificationAtom } from "../../recoil/atoms/notificationAtom.js";
import { styled } from "@mui/material/styles";
import { colorsSemantic } from "../../theme/index.js";
import PropTypes from "prop-types";

const getNotificationTypeIcon = (type) => {
  switch (type) {
    case "damage_booking_diff":
      return <PriceChangeOutlined fontSize="small" />;
    case "damage_booking_released":
      return <PriceCheckOutlined fontSize="small" />;
    case "damage_reminder":
      return <PendingActionsOutlined fontSize="small" />;
    case "new_damage_attachment":
      return <AddPhotoAlternateOutlined fontSize="small" />;
    case "new_damage_form":
      return <PostAddOutlined fontSize="small" />;
    case "unassigned_incoming_mails":
      return <ForwardToInboxOutlined fontSize="small" />;
    case "new_communication":
    case "incoming_mail_assigned":
    default:
      return <MarkEmailUnreadOutlined fontSize="small" />;
  }
};

const getNotificationTypeLabel = (type) => {
  switch (type) {
    case "damage_booking_diff":
      return "Buchung Differenz";
    case "damage_booking_released":
      return "Buchung freigegeben";
    case "damage_reminder":
      return "Schaden Erinnerung";
    case "new_damage_attachment":
      return "Neuer Schadenanhang";
    case "new_damage_form":
      return "Neues Schadenformular";
    case "unassigned_incoming_mails":
      return "Unzugeordnete E-Mails";
    case "new_communication":
      return "Neue Kommunikation";
    case "incoming_mail_assigned":
      return "Neue E-Mail";
    default:
      return type;
  }
};

const StyledBadge = styled(Badge)(({ theme }) => ({
  "& .MuiBadge-badge": {
    backgroundColor: colorsSemantic.icon.accent.gold,
    color: theme.palette.common.white,
    fontSize: "12px",
    fontWeight: 500,
    lineHeight: 1.5,
    minWidth: "22px",
    height: "22px",
    padding: `0 ${theme.spacing(0.75)}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "999px",
    left: theme.spacing(0.5),
  },
}));

export default function NotificationsTable({
  variant = "compact",
  enablePagination = true,
  enableSorting = true,
  enableTopToolbar = true,
  enableBottomToolbar = true,
  enableHeader = false,
  showViewAllLink = true,
  totalCount,
}) {
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useUser();
  const navigate = useNavigate();
  const setNotifications = useSetRecoilState(notificationAtom);

  const isCompact = variant === "compact";
  const isFull = variant === "full";
  const tableKey = "notifications";
  const storageKey = isFull
    ? "v2_table_notifications_full"
    : "v2_table_notifications_compact";
  const defaultRowsPerPage = isCompact ? 3 : getUserRowsPerPage(user, 15);

  const [activeFilterTab, setActiveFilterTab] = useState("all");
  const [statusCounts, setStatusCounts] = useState(null);

  // Build filter based on active tab
  const filter = useMemo(() => {
    if (!isFull) {
      return null;
    }

    switch (activeFilterTab) {
      case "new":
        return { seen: false };
      case "archive":
        return { seen: true };
      default:
        return null;
    }
  }, [isFull, activeFilterTab]);

  const tablesConfig = useMemo(
    () => [
      {
        key: tableKey,
        label: "Benachrichtigungen",
        route: `/data/notifications`,
        responseKey: tableKey,
        pageSize: defaultRowsPerPage,
        storageKey: storageKey,
        extraParams: {
          sort: [{ field: "created_at", order: "desc" }],
          ...(filter ? { filters: filter } : {}),
        },
      },
    ],
    [defaultRowsPerPage, storageKey, filter]
  );

  const { tableStates, updateTableState, fetchData } = useMultiTableData(
    tablesConfig,
    enqueueSnackbar
  );

  const tableState = tableStates[tableKey] || {};

  // Initial fetch for compact variant
  useEffect(() => {
    if (!isCompact) {
      return;
    }
    updateTableState(tableKey, { shouldFetch: true });
  }, [isCompact, tableKey]);

  // Reset pagination and refetch when filter tab changes for full variant
  useEffect(() => {
    if (!isFull) {
      return;
    }
    updateTableState(tableKey, { pageIndex: 0, shouldFetch: true });
  }, [activeFilterTab, isFull, tableKey]);

  // Fetch status counts for filter tabs
  useEffect(() => {
    if (!isFull) {
      return;
    }

    const fetchStatusCounts = async () => {
      const response = await backendGet("/data/notifications/status-counts", {
        label: "Benachrichtigungen Übersicht",
        enqueueSnackbar,
        options: {
          params: {
            apiVersion: "v2",
          },
        },
      });

      if (response?.data) {
        setStatusCounts(response.data);
      }
    };

    fetchStatusCounts();
  }, [enqueueSnackbar, isFull]);

  const rows = tableState.rows || [];

  const handleMarkAsSeen = useCallback(
    async (e, notification) => {
      e.stopPropagation();

      try {
        await backendPost(`data/notifications/${notification.id}/seen`, {
          label: "Notification - gelesen",
          enqueueSnackbar,
        });

        // Update local table state
        const updatedRows = rows.map((n) =>
          n.id === notification.id
            ? { ...n, seen_at: new Date().toISOString() }
            : n
        );
        updateTableState(tableKey, { rows: updatedRows });

        // Update global notification atom
        setNotifications((prev) =>
          prev.map((n) =>
            n.id === notification.id
              ? { ...n, seen_at: new Date().toISOString() }
              : n
          )
        );
      } catch (error) {
        enqueueSnackbar("Notification konnte nicht aktualisiert werden", {
          variant: "error",
        });
      }
    },
    [rows, updateTableState, setNotifications, enqueueSnackbar]
  );

  const handleRowClick = useCallback(
    async (row) => {
      // Mark as seen
      try {
        await backendPost(`data/notifications/${row.id}/seen`, {
          label: "Notification - gelesen",
          enqueueSnackbar,
        });

        // Update global notification atom
        setNotifications((prev) =>
          prev.map((n) =>
            n.id === row.id ? { ...n, seen_at: new Date().toISOString() } : n
          )
        );
      } catch (error) {
        // Silent fail for mark as seen
      }

      // Navigate if URL exists
      if (row.url) {
        navigate(row.url);
      }
    },
    [navigate, setNotifications, enqueueSnackbar]
  );

  // Title component with link to view all notifications
  const title = useMemo(() => {
    if (!showViewAllLink) {
      return null;
    }

    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Benachrichtigungen
          {totalCount > 0 && (
            <StyledBadge
              badgeContent={totalCount}
              invisible={totalCount === 0}
            ></StyledBadge>
          )}
        </Typography>

        <ViewAllLink label="Alle anzeigen" to="/notifications" />
      </Box>
    );
  }, [showViewAllLink, totalCount]);

  // Filter tabs for full variant
  const filterTabs = useMemo(() => {
    if (!isFull) {
      return undefined;
    }

    const totalRows = tableState.totalRows ?? 0;

    return [
      {
        key: "all",
        label: "Alle",
        count: statusCounts?.all ?? totalRows,
        onClick: setActiveFilterTab,
      },
      {
        key: "new",
        label: "Neue",
        count: statusCounts?.new ?? 0,
        onClick: setActiveFilterTab,
      },
      {
        key: "archive",
        label: "Archiv",
        count: statusCounts?.archive ?? 0,
        onClick: setActiveFilterTab,
      },
    ];
  }, [isFull, statusCounts, tableState.totalRows]);

  const notificationsHeaderDefinition = useMemo(
    () => [
      {
        accessorKey: "type",
        header: "Typ",
        size: variant === "full" ? 180 : 40,
        Cell: ({ row }) => (
          <Tooltip title={getNotificationTypeLabel(row.original.type)}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {getNotificationTypeIcon(row.original.type)}
              {variant === "full" && (
                <Typography variant="body2">
                  {getNotificationTypeLabel(row.original.type)}
                </Typography>
              )}
            </Box>
          </Tooltip>
        ),
      },
      {
        accessorKey: "title",
        header: "Betreff",
        size: variant === "full" ? 400 : 250,
        Cell: ({ row }) => (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Circle
              sx={{
                fontSize: 10,
                color: row.original.seen_at
                  ? "text.disabled"
                  : "secondary.main",
                opacity: row.original.seen_at ? 0 : 1,
              }}
            />
            <Typography
              variant="body2"
              sx={{
                fontWeight: row.original.seen_at ? 400 : 600,
                cursor: row.original.url ? "pointer" : "default",
              }}
            >
              {row.original.title}
            </Typography>
          </Box>
        ),
      },
      {
        accessorKey: "created_at",
        header: "Datum",
        size: 150,
        Cell: ({ cell }) => getDatumZeit(cell.getValue()),
      },
      ...(variant === "full"
        ? [
            {
              accessorKey: "actions",
              header: "",
              size: 80,
              enableSorting: false,
              enableColumnFilter: false,
              Cell: ({ row }) => (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  {row.original.intern && (
                    <Tooltip title="intern (für VN nicht sichtbar)">
                      <LockOutlined fontSize="small" color="action" />
                    </Tooltip>
                  )}
                  {!row.original.seen_at && (
                    <Tooltip title="Als gelesen markieren">
                      <IconButton
                        size="small"
                        onClick={(e) => handleMarkAsSeen(e, row.original)}
                      >
                        <VisibilityOffOutlined fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              ),
            },
          ]
        : []),
    ],
    [variant, handleMarkAsSeen]
  );

  return (
    <DataTable
      columns={notificationsHeaderDefinition}
      data={rows}
      isLoading={tableState.isLoading}
      hasLoaded={tableState.hasLoaded}
      title={title}
      pageIndex={tableState.pageIndex}
      pageSize={tableState.pageSize}
      totalRows={tableState.totalRows}
      onChangeSorting={(sorting) => updateTableState(tableKey, { sorting })}
      onChangeFilters={(filters) => updateTableState(tableKey, { filters })}
      onChangePage={(nextOrUpdater) => {
        const base = {
          pageIndex: tableState.pageIndex ?? 0,
          pageSize: tableState.pageSize ?? defaultRowsPerPage,
        };
        const next =
          typeof nextOrUpdater === "function"
            ? nextOrUpdater(base)
            : nextOrUpdater;
        if (!next) {
          return;
        }
        updateTableState(tableKey, {
          pageIndex: next.pageIndex,
          pageSize: next.pageSize,
        });
      }}
      onRowClick={handleRowClick}
      storageKey={storageKey}
      enableSorting={enableSorting}
      enableTopToolbar={enableTopToolbar}
      enablePagination={enablePagination}
      enableHeader={enableHeader}
      enableBottomToolbar={enableBottomToolbar}
      enableGlobalFilter={false}
      enableColumnFilters={false}
      enableColumnHiding={false}
      topToolbarIcons={false}
      variant={variant === "full" ? "enclosed" : "compact"}
      filterTabs={filterTabs}
      activeFilterTab={activeFilterTab}
      muiPaginationProps={{
        rowsPerPageOptions: isCompact ? [3] : [5, 10, 25, 50],
        showRowsPerPage: !isCompact,
      }}
    />
  );
}

NotificationsTable.propTypes = {
  variant: PropTypes.oneOf(["full", "compact"]),
  enableHeader: PropTypes.bool,
  enablePagination: PropTypes.bool,
  enableSorting: PropTypes.bool,
  enableTopToolbar: PropTypes.bool,
  enableBottomToolbar: PropTypes.bool,
  showViewAllLink: PropTypes.bool,
  totalCount: PropTypes.number,
};
